import { Dashboard } from "api/model/dashboard/Dashboard";
import { T } from "services/lang/LanguageService";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import React from "react";
import { handleAddNewTab } from "./ImportDashboardOrTab";
import "../dashboard-styles/DashboardEditor.scss";

interface DashboardEditorProps {
  updateQuery: any;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
  tabId: number;
  triggerTabConfiguratorVisibility: () => void;
  setReorderTabsDialogVisibility: (reorderTabsDialogVisibility: boolean) => void;
  setDashboardConfiguratorVisibility: (dashboardConfiguratorVisibility: boolean) => void;
  setIsImportDashboardDialogVisibility: (isImportDashboardDialogVisible: boolean) => void;
  setAddNewGroupDialogVisibility: (addNewGroupDialogVisibility: boolean) => void;
  isGroupReorderEnabled: boolean;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
  setIsCreateDatasetDialogVisible: (isCreateDatasetDialogVisible: boolean) => void;
  setDatasetSearchDialogVisibility: (isDatasetSearchDialogVisible: boolean) => void;
}

const DashboardEditor = (props: DashboardEditorProps) => {
  const navigate = useNavigate();

  const removeTabConfirmation = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.remove-tab"),
      confirmedCallback: handleRemoveSelectedTab,
    });
  };

  const handleRemoveSelectedTab = () => {
    const updatedCurrentDashboard = {
      ...props.dashboard,
      dashboardTabsJs: [
        ...props.dashboard.dashboardTabsJs.slice(0, props.tabId),
        ...props.dashboard.dashboardTabsJs.slice(props.tabId + 1),
      ],
    };
    props.setDashboard(updatedCurrentDashboard);
    navigate(`/dashboard/${props.dashboard?.id}/tab/0`);
  };

  return (
    <div className="flex flex-row justify-content-between">
      <div className="flex flex-column gap-2">
        <div className="flex flex-row gap-8 mb-1">
          <div className="flex flex-column gap-2">
            <div>{T("word.dashboard")}:</div>
            <div className="flex flex-row gap-2 mb-3">
              <Button
                icon="pi pi-save"
                tooltip={T("action.save-dashboard")}
                className="p-button-info dashboard-editor-button"
                tooltipOptions={{ position: "bottom" }}
                onClick={() => props.updateQuery.mutate(props.dashboard)}
              />
              <Button
                icon="pi pi-cog"
                tooltip={T("action.configure-dashboard")}
                className="p-button-warning dashboard-editor-button"
                onClick={() => props.setDashboardConfiguratorVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2">
            <div>{T("word.tabs")}:</div>
            <div className="flex flex-row gap-2">
              <Button
                icon="pi pi-plus"
                tooltip={T("action.add-new-tab")}
                className="p-button-success dashboard-editor-button"
                tooltipOptions={{ position: "bottom" }}
                onClick={() => handleAddNewTab(props.dashboard, props.setDashboard, "Tab")}
              />
              <Button
                icon="pi pi-cog"
                tooltip={T("action.configure-tab")}
                className="p-button-warning dashboard-editor-button"
                onClick={() => props.triggerTabConfiguratorVisibility()}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                tooltip={T("label.reorder-tabs")}
                icon="pi pi-arrow-right-arrow-left"
                className="p-button-secondary rotate-90 dashboard-editor-button"
                onClick={() => props.setReorderTabsDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-trash"
                tooltip={T("action.remove-tab")}
                tooltipOptions={{ position: "bottom" }}
                className="p-button-danger dashboard-editor-button"
                onClick={removeTabConfirmation}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <div>{T("word.datasets")}:</div>
        <div className="flex flex-row gap-2">
          <Button
            icon="pi pi-plus"
            tooltip={T("action.create-new-dataset")}
            className="p-button-success dashboard-editor-button"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => props.setIsCreateDatasetDialogVisible(true)}
          />
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <div>{T("action.import")}:</div>
        <div className="flex flex-row gap-2">
          <Button
            tooltip={T("action.import-dashboard-or-tab")}
            icon="pi pi-file-import"
            tooltipOptions={{ position: "bottom" }}
            className="p-button-success dashboard-editor-button"
            onClick={() => props.setIsImportDashboardDialogVisibility(true)}
          />
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <div>{T("action.search")}:</div>
        <div className="flex flex-row gap-2">
          <Button
            icon="pi pi-search"
            tooltip={T("action.search-dataset")}
            className="p-button-warning dashboard-editor-button"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => props.setDatasetSearchDialogVisibility(true)}
          />
        </div>
      </div>
      <div className="flex flex-column gap-2">
        <div>{T("label.component-groups")}:</div>
        <div className="flex flex-row justify-content-end gap-2">
          <Button
            tooltip={T("action.add-new-components-group")}
            icon="pi pi-plus"
            className="p-button-success dashboard-editor-button"
            tooltipOptions={{ position: "bottom" }}
            onClick={() => props.setAddNewGroupDialogVisibility(true)}
          />
          <Button
            tooltip={T("action.reorder-components-groups")}
            icon="pi pi-arrow-right-arrow-left"
            tooltipOptions={{ position: "bottom" }}
            className={`${props.isGroupReorderEnabled ? "p-button-warning rotate-90 border dashboard-editor-button" : "p-button-secondary rotate-90 dashboard-editor-button"}`}
            onClick={() => props.setIsGroupReorderEnabled(!props.isGroupReorderEnabled)}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardEditor;
